function normalizeKey(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeAttemptsRemaining(attemptsRemaining) {
  const n = Number(attemptsRemaining);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

function formatOptionalAttemptPhrase(attemptsRemaining) {
  const remaining = normalizeAttemptsRemaining(attemptsRemaining);
  if (remaining <= 0) return "";
  return remaining === 1 ? "once" : `${remaining} more times`;
}

function formatAttemptWindow(attemptsRemaining) {
  const remaining = normalizeAttemptsRemaining(attemptsRemaining);
  if (remaining <= 0) return "";
  return remaining === 1 ? "one remaining attempt" : `${remaining} remaining attempts`;
}

function resolveTemplateKey(lessonMode = {}, evaluationStrategy = {}) {
  const template = normalizeKey(evaluationStrategy?.template);
  if (template) return template;

  const mode = normalizeKey(lessonMode?.mode);
  if (!mode) return "";

  const modeMap = {
    exploratory_creativity: "exploratory_flow",
    freeform_supportive: "exploratory_flow",
  };

  return modeMap[mode] || mode;
}

function getBandLead(scoreBand, templateKey, focus) {
  const band = normalizeKey(scoreBand);
  const focusText = focus || "the lesson goal";

  if (templateKey === "exploratory_flow") {
    if (band === "excellent" || band === "very good") {
      return "Your freewriting flowed naturally and met the lesson objective well.";
    }
    if (band === "good") {
      return "Your freewriting stayed in motion and met the lesson objective.";
    }
    return "Your freewriting kept enough momentum to reach the lesson goal.";
  }

  if (band === "excellent") {
    return `Your ${focusText} is working especially well.`;
  }
  if (band === "very good") {
    return `Your ${focusText} is working well.`;
  }
  if (band === "good") {
    return `You met the lesson goal for ${focusText}.`;
  }
  return `You reached the lesson goal for ${focusText} and have room to build it further.`;
}

function getRetryLead(scoreBand, templateKey, focus) {
  const band = normalizeKey(scoreBand);
  const focusText = focus || "the lesson goal";

  if (templateKey === "exploratory_flow") {
    return "Keep the writing moving and follow the next thought.";
  }

  if (band === "excellent" || band === "very good") {
    return `Your ${focusText} already has strong material to build from.`;
  }
  if (band === "good") {
    return `You're building the ${focusText} well.`;
  }
  return `You're building the ${focusText}.`;
}

const LESSON_COPY = {
  exploratory_flow: {
    focus: "freewriting flow",
    passAction: "refine this draft",
    retryAction: "collect more raw material",
  },
  encouragement_first: {
    focus: "personal motivation",
    passAction: "sharpen the personal detail",
    retryAction: "add one more specific reason for writing",
  },
  sensory_craft: {
    focus: "sensory detail",
    passAction: "deepen the atmosphere",
    retryAction: "add another sensory layer",
  },
  voice_perspective: {
    focus: "voice shift",
    passAction: "push the perspective change further",
    retryAction: "make the voice change more distinct",
  },
  imaginative_construction: {
    focus: "worldbuilding",
    passAction: "expand the world a little further",
    retryAction: "add a more vivid imaginative detail",
  },
  character_depth: {
    focus: "character depth",
    passAction: "push the inner life further",
    retryAction: "add one sharper interior detail",
  },
  structure_control: {
    focus: "story structure",
    passAction: "tighten the turning points",
    retryAction: "make the structure more distinct",
  },
  conflict_control: {
    focus: "conflict",
    passAction: "raise the pressure a little more",
    retryAction: "make the stakes feel sharper",
  },
  dialogue_subtext: {
    focus: "dialogue and subtext",
    passAction: "sharpen the hidden tension",
    retryAction: "make the subtext more legible",
  },
  pacing_control: {
    focus: "pacing",
    passAction: "adjust the tempo a little more",
    retryAction: "shape the pace more deliberately",
  },
  ending_resolution: {
    focus: "ending resolution",
    passAction: "tighten the ending a little further",
    retryAction: "make the resolution land more clearly",
  },
  revision_practice: {
    focus: "revision",
    passAction: "push the revision further",
    retryAction: "strengthen the revised draft again",
  },
  sound_revision: {
    focus: "rhythm and sound",
    passAction: "polish the rhythm a little more",
    retryAction: "smooth the sound and flow",
  },
  feedback_reflection: {
    focus: "reflection",
    passAction: "deepen the reflection",
    retryAction: "make the reflection more specific",
  },
  default: {
    focus: "the lesson goal",
    passAction: "refine the draft",
    retryAction: "strengthen the lesson skill",
  },
};

export function getLessonFooterCopy({
  lessonMode = {},
  evaluationStrategy = {},
  passed = false,
  attemptsRemaining = 0,
  scoreBand = "",
} = {}) {
  const templateKey = resolveTemplateKey(lessonMode, evaluationStrategy);
  const config = LESSON_COPY[templateKey] || LESSON_COPY.default;
  const optionalAttempts = formatOptionalAttemptPhrase(attemptsRemaining);
  const remainingAttempts = formatAttemptWindow(attemptsRemaining);
  const passLead = getBandLead(scoreBand, templateKey, config.focus);
  const retryLead = getRetryLead(scoreBand, templateKey, config.focus);

  if (passed) {
    if (!optionalAttempts) {
      return `${passLead} You can continue to the next lesson.`;
    }

    if (templateKey === "exploratory_flow") {
      return `${passLead} You may refine this draft ${optionalAttempts} if you want to explore the exercise further, or continue to the next lesson.`;
    }

    return `${passLead} You can use your ${remainingAttempts} to ${config.passAction}, or continue to the next lesson.`;
  }

  if (!remainingAttempts) {
    return `${retryLead} Continue to the next lesson when you're ready.`;
  }

  if (templateKey === "exploratory_flow") {
    return `${retryLead} You have ${remainingAttempts} left to build more raw material.`;
  }

  return `${retryLead} You can use ${remainingAttempts} to ${config.retryAction}.`;
}
